import { LinearProgress } from "@mui/material";

import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { apiClient } from "@/core/api/ApiClient";

import GeneralErrorAlert from "../../AppAlerts/GeneralErrorAlert";
import RolesSelector, { RolesSelectorProps } from "./RolesSelector";

export interface TenantRolesSelectorProps extends Omit<
  RolesSelectorProps,
  "getRoles" | "roleList" | "roleAssignmentRestrictions"
> {
  tenantIdentifier?: string | null;
  targetUserId?: string | null;
}

export default function TenantRolesSelector({
  tenantIdentifier,
  targetUserId,
  isRestricted = true,
  ...otherProps
}: TenantRolesSelectorProps) {
  const roleAssignmentRestrictionsRequest = useApiRequest(
    apiClient.tenantAccountApi.apiV1TenantAccountRoleAssignmentsRestrictionsGet,
    {
      nexusOpsTenant: tenantIdentifier || EMPTY_TENANT_IDENTIFIER,
      targetUserId: targetUserId || undefined,
    },
    {
      deps: [tenantIdentifier, targetUserId],
      skip: !isRestricted,
    },
  );
  const roleAssignmentRestrictions = roleAssignmentRestrictionsRequest?.data;

  if (roleAssignmentRestrictionsRequest.isLoading) {
    return <LinearProgress />;
  }
  if (roleAssignmentRestrictionsRequest.isError) {
    return <GeneralErrorAlert title='Error while loading roles' />;
  }

  return (
    <RolesSelector
      getRoles={async () => {
        const response = await apiClient.tenantAccountApi.apiV1TenantAccountRolesGet({
          nexusOpsTenant: tenantIdentifier || EMPTY_TENANT_IDENTIFIER,
        });
        return response.data;
      }}
      roleAssignmentRestrictions={roleAssignmentRestrictions}
      isRestricted={isRestricted}
      {...otherProps}
    />
  );
}
